

























import Vue from 'vue';
import { Toast } from '@/components/ds';
import LoadingPage from '@/components/LoadingPage.vue';
import ProuniHeader from '@/components/prouni/ProuniHeader.vue';
import { getAssetPath } from '@/helpers/assets';
import prouniAuth from '@/helpers/prouniAuth';
import prouniPipeline from '@/pipeline/prouni';
import prouniSteps from '@/pipeline/prouni/steps';
import PROUNI_VIEWS_NAMES from '@/router/prouniViewsNames';
import { ProuniApplication, ProuniApplicationStatus } from '@/store/modules/prouni/types';

const STEP_ORDER = [
  prouniSteps.personalData,
  prouniSteps.personalDocuments,
  prouniSteps.residence,
  prouniSteps.familyIncome,
  prouniSteps.education,
  prouniSteps.acceptance,
];

export default Vue.extend({
  name: 'ProuniStart',
  components: { LoadingPage, ProuniHeader, Toast },
  data() {
    return {
      isLoading: false,
      error: false,
      isLogged: prouniAuth.isLogged(),
      heroImageSrc: getAssetPath('img/prouni/hero-start.svg'),
    };
  },
  mounted() {
    if (!this.isLogged) {
      this.openLogin();
      return;
    }
    this.bootstrap();
  },
  methods: {
    openLogin() {
      prouniAuth.loginWithIframe(
        {
          onSuccess: () => {
            this.isLogged = true;
            this.bootstrap();
          },
          onError: () => {
            this.error = true;
          },
        },
        '.prouni-start__container__content-iframe',
        'prouni-iframe',
      );
    },
    async bootstrap() {
      // The pipeline is a singleton interpreter — if the candidate is simply
      // returning to /prouni, just re-navigate to the active step.
      if (prouniPipeline.initialized) {
        prouniPipeline.navigateToStep(this.$router, prouniPipeline.state);
        return;
      }

      this.isLoading = true;
      this.error = false;
      try {
        // Eligibility gate: only e-mails on the manually-curated ProUni
        // candidate list (SisProUni pre-selection) may start an application.
        const eligibility = await this.$store.dispatch('GET_MY_PROUNI_ELIGIBILITY');
        if (!eligibility?.isEligible) {
          this.$router
            .push({ name: PROUNI_VIEWS_NAMES.PROUNI_NOT_ELIGIBLE })
            .catch(() => undefined);
          return;
        }

        const application: ProuniApplication | null = await this.$store.dispatch(
          'GET_MY_PROUNI_APPLICATION',
        );

        const isTracking =
          application &&
          [
            ProuniApplicationStatus.submitted,
            ProuniApplicationStatus.approved,
            ProuniApplicationStatus.rejected,
          ].includes(application.status as ProuniApplicationStatus);

        if (isTracking) {
          this.$router.push({ name: PROUNI_VIEWS_NAMES.PROUNI_FOLLOW_UP }).catch(() => undefined);
          return;
        }

        // First-time candidate: if there is no ProUni application yet, create a
        // draft up front so the first screen (document uploads) has a record to
        // attach to — getProuniDocumentUploadUrl/submitProuniDocument require it.
        const currentApplication: ProuniApplication =
          application || (await this.$store.dispatch('UPSERT_PROUNI_APPLICATION', {}));

        prouniPipeline.start(this.$router);
        this.resumeTo(currentApplication?.pipelineState?.step);
      } catch (err) {
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },
    resumeTo(lastCompletedStep?: string) {
      if (!lastCompletedStep) return;
      const completedIndex = STEP_ORDER.indexOf(lastCompletedStep);
      if (completedIndex < 0) return;
      // Resume on the step right after the last completed one, replaying NEXT so
      // the pipeline history (and back-navigation) stays consistent.
      const targetIndex = Math.min(completedIndex + 1, STEP_ORDER.length - 1);
      for (let i = 0; i < targetIndex; i += 1) {
        prouniPipeline.next();
      }
    },
  },
});
